import { RequireAccess } from '@/components/require-access'
import { SaveDialog, type SaveToGalleryResult, useAssetHub, useDesignGallery } from '@/managers'
import {
	Button,
	emitUnhandledError,
	logger,
	SelectField,
	SettingsSubheading,
	WidgetInputColorField,
	WidgetInputComplexNumericField,
	WidgetInputTextField,
	WidgetLabel,
	WidgetMicroButton,
	WidgetSelectGroup,
} from '@/ui'
import type { AdDesignShowDefaultsResponse, AdDesignStoreRequest, BrandingData } from '@jobilla/nexus-sdk'
import { roleEnum } from '@jobilla/nexus-sdk'
import type { FontData } from '@jobilla/nexus-sdk'
import { EllipsisIcon } from 'lucide-react'
import { domToPng } from 'modern-screenshot'
import { useId, useRef, useState } from 'react'
import { extractBrandSetName } from '../../../constructs/branding/simplified/extract-brand-set-name'
import {
	AiGeneratedWatermark,
	CanvasContainer,
	type CanvasContainerRef,
	ExternalCanvasData,
	initialCanvasData,
	SafeZone,
} from '../../../editor'
import { CanvasData } from '../../../editor/canvas/data'
import { CanvasExportDialog, CanvasImportDialog } from '../../../editor/widgets/canvas-document-dialogs'
import type { OrgAssets } from '../../shared'
import { SettingsNav } from '../settings-nav'

type SimulatorPreset = 'feed' | 'story' | 'custom'

const presets: Record<Exclude<SimulatorPreset, 'custom'>, { width: number; height: number; safeZone: SafeZone }> = {
	feed: { width: 480, height: 600, safeZone: { top: 0, right: 0, bottom: 0, left: 0 } },
	story: { width: 480, height: 853, safeZone: { top: 100, right: 0, bottom: 120, left: 0 } },
}

const defaultExternalCanvasData: ExternalCanvasData = {
	title: 'Senior Software Engineer',
	subtitle: 'Join our growing team and build the future of recruitment.',
	location: 'Helsinki, Finland',
	cta: 'Apply Now',
	logoUrl: '',
	backgroundUrl: '',
	foregroundColor: '#ffffff',
	backgroundColor: '#1a1a1a',
	accentForegroundColor: '#ffffff',
	accentBackgroundColor: '#6d28d9',
}

export type DesignSimulatorProps = {
	orgAssets: OrgAssets
	onAssetsUpload: (files: FileList, collection?: string) => void
	customFonts?: FontData[]
	customFontsLoading?: boolean
	brands?: BrandingData[]
	defaults?: AdDesignShowDefaultsResponse
	onCreateDesign: (data: AdDesignStoreRequest) => Promise<{ id: string }>
	onUpdateDefaults: (
		data: { defaultFeedAdDesignId: string | null; defaultStoryAdDesignId: string | null },
	) => Promise<void>
	onUploadThumbnail: (file: File) => Promise<{ id: string }>
	onInvalidateDesigns: () => void | Promise<void>
}

export function DesignSimulator({
	orgAssets,
	onAssetsUpload,
	customFonts,
	customFontsLoading,
	brands,
	defaults,
	onCreateDesign,
	onUpdateDefaults,
	onUploadThumbnail,
	onInvalidateDesigns,
}: DesignSimulatorProps) {
	const [preset, setPreset] = useState<SimulatorPreset>('feed')
	const [customWidth, setCustomWidth] = useState(480)
	const [customHeight, setCustomHeight] = useState(600)
	const [customSafeZone, setCustomSafeZone] = useState<SafeZone>({ top: 0, right: 0, bottom: 0, left: 0 })
	const [externalCanvasData, setExternalCanvasData] = useState<ExternalCanvasData>(defaultExternalCanvasData)

	const { width, height, safeZone } = preset === 'custom'
		? { width: customWidth, height: customHeight, safeZone: customSafeZone }
		: presets[preset]

	const canvasRef = useRef<CanvasContainerRef>(null)
	const canvasWrapperRef = useRef<HTMLDivElement>(null)
	const [document, setDocument] = useState<CanvasData>(initialCanvasData)

	// Gallery integration
	const designGallery = useDesignGallery()
	const [saveDialogOpen, setSaveDialogOpen] = useState(false)
	const [importDialogOpen, setImportDialogOpen] = useState(false)
	const [exportDialogOpen, setExportDialogOpen] = useState(false)
	const [saving, setSaving] = useState(false)

	const handleLoadFromGallery = async () => {
		let design: Awaited<ReturnType<typeof designGallery.open>>
		try {
			design = await designGallery.open('picker')
		} catch {
			return // Gallery was aborted (e.g. re-opened by another caller)
		}

		if (!design) { return } // User closed without selecting

		try {
			const parsed = CanvasData.parse(JSON.parse(design.document))
			setDocument(parsed)
			canvasRef.current?.setDocument(parsed)
		} catch (error) {
			logger.error(error, { category: 'canvas' })
			emitUnhandledError({ title: 'Failed to load design', cause: error })
		}
	}

	const handleSaveToGallery = async (result: SaveToGalleryResult) => {
		setSaving(true)

		try {
			let thumbnailMediaId: string | null = null

			// Capture PNG thumbnail
			const el = canvasWrapperRef.current
			if (el) {
				try {
					const pngDataUrl = await domToPng(el, { scale: 1 })
					const response = await fetch(pngDataUrl)
					const blob = await response.blob()
					const file = new File([blob], 'thumbnail.png', { type: 'image/png' })
					const uploaded = await onUploadThumbnail(file)
					thumbnailMediaId = uploaded.id
				} catch (error) {
					logger.error(error, { category: 'canvas' })
				}
			}

			const created = await onCreateDesign({
				name: result.name,
				document: JSON.stringify(document),
				thumbnailMediaId,
			})

			// Set defaults if requested — only call when defaults have loaded
			// so we can preserve the unchanged side's existing value.
			if ((result.setAsDefaultFeed || result.setAsDefaultStory) && defaults) {
				await onUpdateDefaults({
					defaultFeedAdDesignId: result.setAsDefaultFeed
						? created.id
						: (defaults.feed?.id ?? null),
					defaultStoryAdDesignId: result.setAsDefaultStory
						? created.id
						: (defaults.story?.id ?? null),
				})
			}

			onInvalidateDesigns()
			setSaveDialogOpen(false)
		} catch (error) {
			logger.error(error, { category: 'canvas' })
			emitUnhandledError({ title: 'Failed to save design', cause: error })
		} finally {
			setSaving(false)
		}
	}

	const handleImportJson = (doc: CanvasData) => {
		setDocument(doc)
		canvasRef.current?.setDocument(doc)
		setImportDialogOpen(false)
	}

	const downloadAsPng = () => {
		const el = canvasWrapperRef.current
		if (!el) { return }
		domToPng(el, { scale: 3 }).then((png) => {
			const a = window.document.createElement('a')
			a.href = png
			a.download = 'canvas.png'
			a.click()
		}).catch((error) => {
			logger.error(error, { category: 'canvas' })
			emitUnhandledError({ title: 'Failed to export image', cause: error })
		})
	}

	return (
		<SettingsNav active="design-simulator">
			<section className="space-y-6 mt-8 pr-8 max-w-[800px]">
				<SettingsSubheading>Design Simulator</SettingsSubheading>
				<div className="text-sm text-blue-600 bg-blue-50 p-2">
					This is a technology preview feature. It will be integrated into the main campaign editor soon.
				</div>
				<RequireAccess
					role={roleEnum.jobillan}
					fallback={<p className="text-sm text-gray-500">You do not have permission to access this page.</p>}
				>
					<div className="flex gap-6">
						<div className="shrink-0">
							<div className="mb-4 space-y-3" style={{ maxWidth: `${width}px` }}>
								<WidgetSelectGroup
									value={preset}
									onChange={setPreset}
									items={[
										{ id: 'feed', label: 'Feed Ad', render: () => 'Feed Ad' },
										{ id: 'story', label: 'Story Ad', render: () => 'Story Ad' },
										{ id: 'custom', label: 'Custom', render: () => 'Custom' },
									]}
								/>
								{preset === 'custom' && (
									<div className="space-y-3">
										<div className="grid grid-cols-2 gap-4">
											<WidgetLabel label="Width">
												<WidgetInputComplexNumericField
													value={customWidth}
													onChange={setCustomWidth}
													suffix="px"
													label="Width"
													defaultValue={480}
												/>
											</WidgetLabel>
											<WidgetLabel label="Height">
												<WidgetInputComplexNumericField
													value={customHeight}
													onChange={setCustomHeight}
													suffix="px"
													label="Height"
													defaultValue={600}
												/>
											</WidgetLabel>
										</div>
										<div className="grid grid-cols-4 gap-2">
											<WidgetLabel label="Top">
												<WidgetInputComplexNumericField
													value={customSafeZone.top}
													onChange={(v) => setCustomSafeZone((prev) => ({ ...prev, top: v }))}
													suffix="px"
													label="Top"
													defaultValue={0}
												/>
											</WidgetLabel>
											<WidgetLabel label="Right">
												<WidgetInputComplexNumericField
													value={customSafeZone.right}
													onChange={(v) =>
														setCustomSafeZone((prev) => ({ ...prev, right: v }))}
													suffix="px"
													label="Right"
													defaultValue={0}
												/>
											</WidgetLabel>
											<WidgetLabel label="Bottom">
												<WidgetInputComplexNumericField
													value={customSafeZone.bottom}
													onChange={(v) =>
														setCustomSafeZone((prev) => ({ ...prev, bottom: v }))}
													suffix="px"
													label="Bottom"
													defaultValue={0}
												/>
											</WidgetLabel>
											<WidgetLabel label="Left">
												<WidgetInputComplexNumericField
													value={customSafeZone.left}
													onChange={(v) =>
														setCustomSafeZone((prev) => ({ ...prev, left: v }))}
													suffix="px"
													label="Left"
													defaultValue={0}
												/>
											</WidgetLabel>
										</div>
									</div>
								)}
							</div>
							<div ref={canvasWrapperRef} className="w-fit relative">
								<CanvasContainer
									ref={canvasRef}
									width={width}
									height={height}
									document={document}
									onDocumentChange={setDocument}
									externalCanvasData={externalCanvasData}
									onExternalCanvasDataChange={(partial) =>
										setExternalCanvasData((prev) => ({ ...prev, ...partial }))}
									safeZone={safeZone}
									orgAssets={orgAssets}
									onAssetsUpload={onAssetsUpload}
									customFonts={customFonts}
									customFontsLoading={customFontsLoading}
								/>
								<AiGeneratedWatermark />
							</div>
							<div className="mt-4 flex flex-wrap gap-2" style={{ maxWidth: `${width}px` }}>
								<Button type="committal" size="tiny" onClick={handleLoadFromGallery}>
									Load from Gallery
								</Button>
								<Button type="committal" size="tiny" onClick={() => setSaveDialogOpen(true)}>
									Save to Gallery
								</Button>
							</div>
							<div className="mt-4 flex flex-wrap gap-2" style={{ maxWidth: `${width}px` }}>
								<Button type="subtle" size="tiny" onClick={() => setImportDialogOpen(true)}>
									Import JSON
								</Button>
								<Button type="subtle" size="tiny" onClick={() => setExportDialogOpen(true)}>
									Export JSON
								</Button>
							</div>
						</div>
						<ExternalCanvasDataEditor
							externalCanvasData={externalCanvasData}
							onExternalCanvasDataChange={(partial) =>
								setExternalCanvasData((prev) => ({ ...prev, ...partial }))}
							onDownload={downloadAsPng}
							brands={brands}
						/>
					</div>
				</RequireAccess>

				<SaveDialog
					open={saveDialogOpen}
					onClose={() => setSaveDialogOpen(false)}
					onSave={handleSaveToGallery}
					saving={saving}
				/>
				<CanvasImportDialog
					open={importDialogOpen}
					onClose={() => setImportDialogOpen(false)}
					onLoad={handleImportJson}
				/>
				<CanvasExportDialog
					open={exportDialogOpen}
					onClose={() => setExportDialogOpen(false)}
					document={JSON.stringify(document)}
				/>
			</section>
		</SettingsNav>
	)
}

function UrlFieldWithAssetPicker(
	{ label, value, onChange, placeholder }: {
		label: string
		value: string
		onChange: (value: string) => void
		placeholder?: string
	},
) {
	const inputId = useId()
	const assetHub = useAssetHub()

	const handlePickFromAssets = async () => {
		try {
			const asset = await assetHub.open()
			if (asset) {
				onChange(asset.url)
			}
		} catch (error) {
			// The picker rejects with an AbortError when re-opened by another
			// caller — that's expected; anything else is a real failure worth
			// recording.
			if (error instanceof DOMException && error.name === 'AbortError') {
				return
			}
			logger.error(error, {
				tags: { component: 'DesignSimulator', action: 'open-asset-hub' },
			})
		}
	}

	return (
		<WidgetLabel label={label} htmlFor={inputId}>
			<div className="flex items-center gap-1">
				<div className="flex-1 min-w-0">
					<WidgetInputTextField
						id={inputId}
						value={value}
						onChange={onChange}
						placeholder={placeholder}
					/>
				</div>
				<WidgetMicroButton onClick={handlePickFromAssets} title="Pick from assets">
					<EllipsisIcon className="size-3.5" />
				</WidgetMicroButton>
			</div>
		</WidgetLabel>
	)
}

function ExternalCanvasDataEditor(
	{ externalCanvasData, onExternalCanvasDataChange, onDownload, brands }: {
		externalCanvasData: ExternalCanvasData
		onExternalCanvasDataChange: (externalCanvasData: Partial<ExternalCanvasData>) => void
		onDownload: () => void
		brands?: BrandingData[]
	},
) {
	const [sampledBrand, setSampledBrand] = useState<BrandingData | null>(null)

	const handleBrandSample = (brand: BrandingData | null) => {
		if (!brand) { return }
		setSampledBrand(brand)
		onExternalCanvasDataChange({
			foregroundColor: brand.foreground,
			backgroundColor: brand.background,
			accentForegroundColor: brand.accentForeground,
			accentBackgroundColor: brand.accentBackground,
			logoUrl: brand.logo?.url ?? '',
		})
	}

	return (
		<div className="grow space-y-6 min-w-[280px]">
			<fieldset className="space-y-3">
				<legend className="text-xs font-medium text-grey-500 uppercase tracking-wide mb-2">Content</legend>
				<WidgetLabel label="Title">
					<WidgetInputTextField
						value={externalCanvasData.title}
						onChange={(value) => onExternalCanvasDataChange({ title: value })}
					/>
				</WidgetLabel>
				<WidgetLabel label="Subtitle">
					<WidgetInputTextField
						value={externalCanvasData.subtitle}
						onChange={(value) => onExternalCanvasDataChange({ subtitle: value })}
					/>
				</WidgetLabel>
				<WidgetLabel label="Location">
					<WidgetInputTextField
						value={externalCanvasData.location}
						onChange={(value) => onExternalCanvasDataChange({ location: value })}
					/>
				</WidgetLabel>
				<WidgetLabel label="CTA">
					<WidgetInputTextField
						value={externalCanvasData.cta}
						onChange={(value) => onExternalCanvasDataChange({ cta: value })}
					/>
				</WidgetLabel>
			</fieldset>

			{brands && brands.length > 0 && (
				<fieldset className="space-y-3">
					<legend className="text-xs font-medium text-grey-500 uppercase tracking-wide mb-0">Brand</legend>
					<SelectField<BrandingData | null>
						options={brands}
						value={sampledBrand}
						onChange={handleBrandSample}
						placeholder="Sample from brand..."
						renderOption={(brand) =>
							brand && (
								<div className="flex items-center justify-between gap-3 w-full">
									<div className="flex items-center gap-2 min-w-0">
										{brand.logo?.url && (
											<span
												className="h-5 w-5 shrink-0 bg-center bg-contain bg-no-repeat rounded"
												style={{ backgroundImage: `url('${brand.logo.url}')` }}
											/>
										)}
										<span className="truncate text-sm">
											{extractBrandSetName(brand.name, brand.website ?? '')}
										</span>
									</div>
									<div className="flex items-center gap-0.5 shrink-0">
										{[
											brand.background,
											brand.foreground,
											brand.accentBackground,
											brand.accentForeground,
										].map((color, index) => (
											<span
												key={index}
												className="h-3 w-3 rounded-sm ring-1 ring-grey-200/60"
												style={{ backgroundColor: color }}
											/>
										))}
									</div>
								</div>
							)}
					/>
				</fieldset>
			)}

			<fieldset className="space-y-3">
				<legend className="text-xs font-medium text-grey-500 uppercase tracking-wide mb-2">Media</legend>
				<UrlFieldWithAssetPicker
					label="Logo URL"
					value={externalCanvasData.logoUrl}
					onChange={(value) => onExternalCanvasDataChange({ logoUrl: value })}
					placeholder="https://..."
				/>
				<UrlFieldWithAssetPicker
					label="Background URL"
					value={externalCanvasData.backgroundUrl}
					onChange={(value) => onExternalCanvasDataChange({ backgroundUrl: value })}
					placeholder="https://..."
				/>
			</fieldset>

			<fieldset className="space-y-3">
				<legend className="text-xs font-medium text-grey-500 uppercase tracking-wide mb-2">Colors</legend>
				<div className="grid grid-cols-2 gap-3">
					<WidgetLabel label="Background">
						<WidgetInputColorField
							value={externalCanvasData.backgroundColor}
							onChange={(value) => onExternalCanvasDataChange({ backgroundColor: value })}
						/>
					</WidgetLabel>
					<WidgetLabel label="Foreground">
						<WidgetInputColorField
							value={externalCanvasData.foregroundColor}
							onChange={(value) => onExternalCanvasDataChange({ foregroundColor: value })}
						/>
					</WidgetLabel>
					<WidgetLabel label="Accent Background">
						<WidgetInputColorField
							value={externalCanvasData.accentBackgroundColor}
							onChange={(value) => onExternalCanvasDataChange({ accentBackgroundColor: value })}
						/>
					</WidgetLabel>
					<WidgetLabel label="Accent Foreground">
						<WidgetInputColorField
							value={externalCanvasData.accentForegroundColor}
							onChange={(value) => onExternalCanvasDataChange({ accentForegroundColor: value })}
						/>
					</WidgetLabel>
				</div>
			</fieldset>

			<div>
				<Button type="subtle" size="short" onClick={onDownload}>
					Download as PNG
				</Button>
			</div>
		</div>
	)
}
